/**
 * What a plan comes to once the cycle and the tax are counted in.
 *
 * The cards upstairs quote a price per month, because that is the only figure
 * two cycles can be compared on. What will actually be taken is a different
 * number: a whole year of it on the yearly cycle, plus GST on top.
 *
 * ## This is no longer what the checkout charges
 *
 * `POST /billing/quote/` is — see `quoteService`. The server knows what a
 * coupon is worth and which side of a state line the supply falls, and the
 * figure printed under Pay now has to be the figure that will be charged.
 *
 * What is left here is the same arithmetic for the screens that have no quote
 * to read: the result page, receipting a payment the backend has not confirmed
 * yet. It agrees with the quote on an order with no coupon on it, and where it
 * cannot it is the quote that is right.
 *
 * The cycle's own price is taken from the plan rather than multiplied out of
 * its monthly figure — the backend quotes both, and what it will raise an
 * invoice for is the one that matters.
 */

import type {
  BillingCycleId,
  PriceBreakdown,
  SubscriptionPlan,
} from "./types/billing.types";

/** How many months each cycle covers — what the summary says is being bought,
 *  and how far ahead the renewal date is set. */
export const MONTHS_PER_CYCLE: Record<BillingCycleId, number> = {
  monthly: 1,
  quarterly: 3,
  "half-yearly": 6,
  yearly: 12,
};

/**
 * GST on software sold as a service in India. A constant because it is the
 * same for every plan and every state — Naurist is the supplier, so nothing
 * here depends on where the dietitian is.
 *
 * When there is a backend it decides this, not the browser: a rate that
 * changes with a budget must not be a number shipped in a bundle that someone
 * is still running from cache. Until then, the figure the invoice would be
 * raised at.
 */
export const GST_RATE = 0.18;

/**
 * Money, to the paisa. Tax on ₹4,788 is ₹861.84 and floating point makes that
 * ₹861.8400000000001 — which is invisible in a total and unmistakable in a
 * line item.
 */
const round2 = (amount: number): number => Math.round(amount * 100) / 100;

/**
 * The same day, a number of months later — where a month later has no such
 * date, the last day of the month it lands in.
 *
 * `setMonth` alone rolls 31 January forward to 3 March, which would tell
 * someone buying on the 31st that they renew on a day in the month after the
 * one they are owed. Clamping to the 28th is what every subscription does with
 * that date, and it is the answer that cannot be wrong in the customer's
 * favour or ours.
 */
const monthsAfter = (from: Date, months: number): Date => {
  const date = new Date(from);
  const dayOfMonth = date.getDate();

  date.setDate(1);
  date.setMonth(date.getMonth() + months);

  // The last day of the month now landed on — day 0 of the next one.
  const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  date.setDate(Math.min(dayOfMonth, lastDay));

  return date;
};

/**
 * The day a cycle bought today runs out.
 *
 * Its own export because the backend prices a plan but says nothing about when
 * it renews — see `quoteService`, which reads the figures off the wire and
 * still has to date the summary's second milestone. The arithmetic is the same
 * either way, which is the whole reason it lives here.
 */
export const renewalOf = (
  cycle: BillingCycleId,
  from: Date = new Date()
): Date => monthsAfter(from, MONTHS_PER_CYCLE[cycle]);

export interface PriceOptions {
  /**
   * The day the purchase is dated. Today, except in a test — a renewal date
   * worked out from the real clock could not be asserted on.
   */
  from?: Date;
  /** What an accepted coupon takes off the subtotal, in rupees. */
  discount?: number;
}

/**
 * What this plan costs to buy today: the months it covers, what they come to,
 * what a coupon took off, the tax on what is left, the total, and the day the
 * cycle runs out.
 *
 * The tax is charged on the discounted amount, not the list one. That is the
 * order the figures have to be worked out in — a coupon reduces the price of
 * the supply, and the tax follows the price — and it is why the summary can
 * show a subtotal, a discount and a tax line that visibly add up to the total.
 *
 * A discount larger than the subtotal is floored at it rather than rejected:
 * whatever eventually issues coupons is what decides which are valid, and this
 * refusing to price one would only mean a checkout that cannot draw itself.
 */
export const priceBreakdown = (
  plan: SubscriptionPlan,
  { from = new Date(), discount = 0 }: PriceOptions = {}
): PriceBreakdown => {
  const months = MONTHS_PER_CYCLE[plan.id];
  const subtotal = round2(plan.cyclePrice);
  const applied = Math.min(Math.max(discount, 0), subtotal);
  const taxableAmount = round2(subtotal - applied);
  const tax = round2(taxableAmount * GST_RATE);

  return {
    months,
    subtotal,
    discount: applied,
    taxRate: GST_RATE,
    taxableAmount,
    tax,
    total: round2(taxableAmount + tax),
    renewsOn: monthsAfter(from, months),
  };
};

/**
 * A figure on the summary — `₹ 4,788`, `₹ 861.84`.
 *
 * Grouped the Indian way like `formatPrice`, which is what every other rupee
 * in the app is written by, but showing the paise where there are any: a tax
 * line rounded to whole rupees would not add up to the total printed under it,
 * and a total that does not equal the sum of the lines above it is the one
 * thing a checkout cannot get away with. Whole amounts still read as whole
 * ones, so nothing carries a pointless `.00`.
 */
export const formatMoney = (amount: number): string =>
  `₹ ${amount.toLocaleString("en-IN", {
    minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;

/** How a duration row names itself: "1 month", "3 months", "12 months". */
export const monthsLabel = (months: number): string =>
  `${months} month${months === 1 ? "" : "s"}`;

/**
 * The same term as the thing being bought: "1 month plan", "3 month plan".
 *
 * Singular "month" however many there are, because here it is modifying
 * "plan" rather than counting anything — "3 months plan" is not a phrase
 * anybody writes. That is the whole reason this is not `monthsLabel` with a
 * word after it, and why the button, the order summary and the receipt all
 * call one plan by this one name.
 */
export const planTermLabel = (months: number): string =>
  `${months} month plan`;
